import { createSignal } from "solid-js"
import type { useLanguage } from "@/context/language"
import { localDevHeaders } from "@/datox/local-auth"
import type { TablerName } from "@/datox/v2/tabler-icons"
import { workspacePath, type FactsheetWorkspace } from "@/datox/workspaces"

// The V2 sidebar's rules (DESIGN-LOG §57, mock 67 `__mock-rail-stripe`, variant A), pure: what it lists, which row is
// the current page, which Workspace tools group is open, and Recent, the last factsheets this person opened. The
// shell (components/datox-v2-shell.tsx) draws them. Everything it remembers lives in this browser, per user, under the
// key pattern of the collapsed state (`dx-v2-sidebar:<user>`); blocked storage just means nothing is remembered.

type Key = Parameters<ReturnType<typeof useLanguage>["t"]>[0]
export type RailItem = { key: Key; icon: TablerName; path: string; also?: string[] }
export type RailGroup = { id: string; key: Key; icon: TablerName; items: RailItem[] }
export type RecentReport = { projectID: string; workspaceID: string; targetID?: string; name: string }
type Store = Pick<Storage, "getItem" | "setItem">

const RECENT = 3

// Bobur's grouping: the four everyday places first, everything else in Workspace tools.
export const RAIL_MAIN: RailItem[] = [
  { key: "datox.v2.nav.reports", icon: "file-analytics", path: "/reports", also: ["/factsheets"] },
  { key: "datox.catalog.workflows", icon: "git-branch", path: "/catalog/workflows" },
  { key: "datox.suite.dataSources", icon: "folder", path: "/files" },
  { key: "datox.chats.title", icon: "messages", path: "/chats" },
]

// "Operations" is "Preparation and operations" shortened, so it fits the 220px row with its chevron.
export const RAIL_GROUPS: RailGroup[] = [
  {
    id: "outputs",
    key: "datox.v2.nav.outputsGroup",
    icon: "inbox",
    items: [
      // "All outputs" (/overview) is gone from the bank's menu: it redirects to Reports, the one list (UX research P7).
      { key: "datox.v2.nav.reportFiles", icon: "file-text", path: "/catalog/reports" },
      { key: "datox.catalog.dashboards", icon: "layout-dashboard", path: "/catalog/dashboards" },
      { key: "datox.approvals.title", icon: "checks", path: "/approvals" },
    ],
  },
  {
    id: "operations",
    key: "datox.v2.nav.operations",
    icon: "stack-2",
    items: [
      { key: "home.projects", icon: "folders", path: "/projects" },
      { key: "datox.automations.title", icon: "clock", path: "/automations" },
      { key: "datox.integrations.title", icon: "plug-connected", path: "/integrations" },
    ],
  },
  {
    id: "configuration",
    key: "datox.v2.nav.configuration",
    icon: "adjustments-horizontal",
    items: [
      { key: "datox.v2.nav.templates", icon: "template", path: "/templates" },
    ],
  },
]

export const isCurrent = (item: RailItem, pathname: string) =>
  [item.path, ...(item.also ?? [])].some((path) => pathname === path || pathname.startsWith(`${path}/`))

export const currentGroup = (pathname: string) => RAIL_GROUPS.find((group) => group.items.some((item) => isCurrent(item, pathname)))?.id

// Arriving on a tool page opens its group; the person can still close it there.
export const openFor = (open: string[], pathname: string) => {
  const id = currentGroup(pathname)
  return !id || open.includes(id) ? open : [...open, id]
}

export const toggleGroup = (open: string[], id: string) => (open.includes(id) ? open.filter((item) => item !== id) : [...open, id])

// Whose memory in this browser: the signed-in account, else the local dev user.
export const railUser = (accountEmail: string) =>
  accountEmail || localDevHeaders()["X-User-Email"] || localDevHeaders()["X-User-Id"] || "default"

export const readGroups = (user: string, storage?: Store) =>
  readList(`dx-v2-tools:${user}`, storage).filter((id): id is string => RAIL_GROUPS.some((group) => group.id === id))

export const writeGroups = (user: string, open: string[], storage?: Store) => writeList(`dx-v2-tools:${user}`, open, storage)

// Newest first, at most three. One row per factsheet (a report and its client), and one per name: a row shows only
// the client, so Hamad's factsheet in another period or report would read as the same row twice.
export const addRecent = (list: RecentReport[], entry: RecentReport) =>
  [entry, ...list.filter((item) => !(item.workspaceID === entry.workspaceID && item.targetID === entry.targetID) && item.name !== entry.name)].slice(0, RECENT)

// What is no longer in the reports list goes: the report, or its client in it. Another company's reports are not in
// this company's list either, so a company switch starts its own Recent.
export const keepExisting = (list: RecentReport[], workspaces: FactsheetWorkspace[]) =>
  list.filter((item) =>
    workspaces.some((workspace) =>
      workspace.id === item.workspaceID &&
      workspace.project_id === item.projectID &&
      (!item.targetID || workspace.config.targets.some((target) => target.id === item.targetID)),
    ),
  )

// The report in the editor with its client picked (it opens in Preview, the editor's reading view).
export const recentPath = (entry: RecentReport) => {
  const path = workspacePath({ project_id: entry.projectID, id: entry.workspaceID })
  return entry.targetID ? `${path}?${new URLSearchParams({ target: entry.targetID })}` : path
}

export const readRecent = (user: string, storage?: Store) => readList(`dx-v2-recent:${user}`, storage).filter(isRecent).slice(0, RECENT)

// The rail's live list: read again whenever the editor or Reports change it.
const [recentVersion, setRecentVersion] = createSignal(0)
export const recentReports = (user: string) => {
  recentVersion()
  return readRecent(user)
}

export function rememberReport(user: string, entry: RecentReport, storage?: Store) {
  saveRecent(user, readRecent(user, storage), (list) => addRecent(list, entry), storage)
}

export function pruneRecent(user: string, workspaces: FactsheetWorkspace[], storage?: Store) {
  saveRecent(user, readRecent(user, storage), (list) => keepExisting(list, workspaces), storage)
}

function saveRecent(user: string, list: RecentReport[], change: (list: RecentReport[]) => RecentReport[], storage?: Store) {
  const next = change(list)
  if (JSON.stringify(next) === JSON.stringify(list)) return
  writeList(`dx-v2-recent:${user}`, next, storage)
  setRecentVersion((value) => value + 1)
}

function isRecent(value: unknown): value is RecentReport {
  if (!value || typeof value !== "object") return false
  const item = value as Record<string, unknown>
  return typeof item.projectID === "string" && typeof item.workspaceID === "string" && typeof item.name === "string" && !!item.name &&
    (item.targetID === undefined || typeof item.targetID === "string")
}

// Private mode, blocked or full storage: an empty list, and nothing kept.
function readList(key: string, storage?: Store): unknown[] {
  try {
    const value: unknown = JSON.parse((storage ?? localStorage).getItem(key) ?? "[]")
    return Array.isArray(value) ? value : []
  } catch {
    return []
  }
}

function writeList(key: string, list: unknown[], storage?: Store) {
  try {
    ;(storage ?? localStorage).setItem(key, JSON.stringify(list))
  } catch {
    return
  }
}
